import { useMutation } from '@apollo/client'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import ListIcon from '@mui/icons-material/List'
import { List, ListItemText } from '@mui/material'
import Collapse from '@mui/material/Collapse'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import { filter } from 'graphql-anywhere'
import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import CalendarListHeader from './CalendarListHeader'
import CalendarListItem from './CalendarListItem'
import { CalendarDataFragment } from '../../generated/graphql'
import { useDeleteMutation } from '../../helpers/GqlHelper'
import { useBranchFilteredQuery } from '../../hooks/BranchFilterHook'
import { useCalendars } from '../../providers/CalendarContext'
import Calendars from '../../schema/Calendars'
import CalendarDialog from '../../screens/calendar/CalendarDialog'
import ConfirmDialog from '../dialogs/ConfirmDialog'

interface DefaultCalendar {
  id: string
}

interface SelectAllModifyParams {
  optionSelected: boolean
  defaultMyCalendar: string
}

const CalendarsList: React.FC = () => {
  const { t } = useTranslation()
  const [isMyCalendarsOpen, setIsMyCalendarsOpen] = useState(false)
  const [editingCalendar, setEditingCalendar] = useState<CalendarDataFragment | null>(null)
  const [calendarToDelete, setCalendarToDelete] = useState<CalendarDataFragment | null>(null)
  const branchFilter = useBranchFilteredQuery()
  const [deleteCalendar] = useDeleteMutation(Calendars.GET_CALENDARS, branchFilter.variables)
  const [editCalendar] = useMutation<CalendarDataFragment>(Calendars.EDIT_CALENDAR)
  const { calendars, toggleSelectedCalendar } = useCalendars()
  const [defaultCalendar, setDefaultCalendar] = useState<DefaultCalendar>({ id: 'default' })
  const [isAllSelected, setIsAllSelected] = useState(false)

  const handleDeleteCalendar = useCallback(
    async (calendar: CalendarDataFragment) => {
      await deleteCalendar({
        variables: {
          input: {
            id: calendar.id,
          },
        },
      })
    },
    [deleteCalendar],
  )

  const handleEditCalendar = useCallback(
    async (calendar: CalendarDataFragment) => {
      await editCalendar({
        variables: { input: filter(Calendars.fragments.CalendarInput, calendar) },
      })
    },
    [editCalendar],
  )

  const handleSetDefaultCalendar = useCallback((calendar: CalendarDataFragment) => {
    setDefaultCalendar(calendar)
  }, [])

  const handleToggleAllCalendars = useCallback(
    (event: React.MouseEvent<HTMLElement>, shouldSelectAll: boolean) => {
      setIsAllSelected(shouldSelectAll)
      const actionParams: SelectAllModifyParams = {
        optionSelected: shouldSelectAll,
        defaultMyCalendar: defaultCalendar.id === 'default' ? calendars[0]?.id : defaultCalendar.id,
      }
      toggleSelectedCalendar(actionParams)
    },
    [calendars, defaultCalendar.id, toggleSelectedCalendar],
  )

  const handleCloseDeleteDialog = useCallback(() => setCalendarToDelete(null), [])
  const handleCloseEditDialog = useCallback(() => setEditingCalendar(null), [])

  return (
    <>
      <ListItem
        button
        onClick={() => setIsMyCalendarsOpen((prev) => !prev)}
        aria-expanded={isMyCalendarsOpen}
        aria-label={t('My calendars')}
      >
        <ListItemIcon>
          <ListIcon />
        </ListItemIcon>
        <ListItemText primary={t('My calendars')} />
        {isMyCalendarsOpen ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={isMyCalendarsOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding dense>
          <CalendarListHeader
            isAllSelected={isAllSelected}
            onToggle={(event) => handleToggleAllCalendars(event, !isAllSelected)}
          />

          {calendars.map((calendar) => (
            <CalendarListItem
              key={calendar.id}
              calendar={calendar}
              checked={calendar.isSelected}
              toggleChecked={() => toggleSelectedCalendar(calendar.id)}
              setDefaultAction={handleSetDefaultCalendar}
              deleteAction={() => setCalendarToDelete(calendar)}
              editAction={() => setEditingCalendar(calendar)}
            />
          ))}
        </List>
      </Collapse>

      {editingCalendar && (
        <CalendarDialog
          open={true}
          calendar={editingCalendar}
          onClose={handleCloseEditDialog}
          commit={handleEditCalendar}
        />
      )}

      <ConfirmDialog
        open={!!calendarToDelete}
        onClose={handleCloseDeleteDialog}
        onConfirm={() => {
          if (calendarToDelete) {
            handleDeleteCalendar(calendarToDelete)
            handleCloseDeleteDialog()
          }
        }}
        title={t('Delete calendar')}
        text={t('Are you sure you want to delete this calendar? All related events will be deleted')}
      />
    </>
  )
}

export default CalendarsList
